/**
 * Form routes use `titleSlug_id` segments (same idea as user management `name_id`).
 * The real id for API calls is everything after the first `_`.
 */

const UUID_PATH_SEGMENT_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Kebab-case path segments under `/forms/...` → breadcrumb label */
export const FORM_ROUTE_SEGMENT_LABELS: Record<string, string> = {
  'form-answers': 'Form-Answers',
  'my-forms': 'My Forms',
  'form-templates': 'Form Templates',
  'form-to-answer': 'Form to answer',
  'forms-to-answer': 'Forms to answer',
  'new-form': 'New form',
  'new-template': 'New template',
};

export function isUuidPathSegment(segment: string): boolean {
  return UUID_PATH_SEGMENT_RE.test(segment);
}

export function formTitleToUrlSlug(title: string): string {
  const s = title
    .trim()
    .replace(/_/g, '-')
    .replace(/\s+/g, '-')
    .replace(/[^\p{L}\p{N}-]/gu, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return s || 'form';
}

export function buildFormUrlSegment(title: string, id: string): string {
  return `${formTitleToUrlSlug(title)}_${id}`;
}

export function parseFormUrlSegment(segment: string): { id: string; titleSlug: string | null } {
  const idx = segment.indexOf('_');
  if (idx === -1) {
    return { id: segment, titleSlug: null };
  }
  return {
    titleSlug: segment.slice(0, idx),
    id: segment.slice(idx + 1),
  };
}

/** Breadcrumb: turn `My-Form-Title` into `My Form Title` */
export function formTitleSlugToDisplayName(titleSlug: string): string {
  return titleSlug.replace(/-/g, ' ');
}

export interface BreadcrumbNamePartFormat {
  text: string;
  /** When true, apply CSS capitalize (short static segments like `summary`). */
  shouldCapitalize: boolean;
}

/**
 * Display text and typography hint for one URL path segment's "name" part
 * (after `split('_')[0]`, same convention as AppBreadcrumbs).
 */
export function formatBreadcrumbNamePart(
  namePart: string,
  extraAliases?: ReadonlyArray<{ url: string; name: string }>
): BreadcrumbNamePartFormat {
  if (FORM_ROUTE_SEGMENT_LABELS[namePart]) {
    return { text: FORM_ROUTE_SEGMENT_LABELS[namePart], shouldCapitalize: false };
  }
  const alias = extraAliases?.find(item => item.url === namePart);
  if (alias) {
    return { text: alias.name, shouldCapitalize: false };
  }
  const decoded = namePart.replace(/%20/g, ' ');
  if (isUuidPathSegment(decoded)) {
    return { text: decoded, shouldCapitalize: false };
  }
  const text = formTitleSlugToDisplayName(decoded);
  return { text, shouldCapitalize: !decoded.includes('-') };
}
